import React, { useContext, useEffect, useState, useRef } from "react"
import {
	Box,
	Button,
	Heading,
	HStack,
	IconButton,
	Modal,
	ModalBody,
	ModalCloseButton,
	ModalContent,
	ModalFooter,
	ModalHeader,
	ModalOverlay,
	Text,
	Stack,
	Table,
	Tag,
	TagLabel,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	useDisclosure,
} from "@chakra-ui/react"
import { UpDownIcon, EditIcon } from "@chakra-ui/icons"
import { Select } from "chakra-react-select"
import { db } from "../../firebase"
import { dateToFormat } from "../../Utilities"
import { useNavigate, NavLink } from "react-router-dom"
import {
	collection,
	endBefore,
	getDocs,
	query,
	where,
	startAfter,
	limit,
	orderBy,
} from "firebase/firestore"
import { JobRequestsMobileCard } from "../../Components/JobRequestsMobileCard"
import { serviceTypesOptions } from "../../Options/ServiceTypes"
import { BlackDomeStatuses } from "../../Options/BlackDomeStatuses"
import { EmptyState } from "../../Components/EmptyState"
import { UserContext } from "../../Contexts/UserContext"
import { AgentPayWallModal } from "../../Components/AgentPayWall"
import { PaywallBanner } from "../../Components/PaywallBanner"
import { SkeletonTable } from "../../Components/SkeletonTable"
import { JOB_REQUEST_LIMIT } from "../../enums"
import { Partners } from "../../partnershipData"

const AgentJobRequestListView = () => {
	const { isOpen, onOpen, onClose } = useDisclosure()
	const { userPlus } = useContext(UserContext)
	const navigate = useNavigate()
	const [page, setPage] = useState(1)
	const pageSize = 10
	const [lastVisible, setLastVisible] = useState(null)
	const [firstVisible, setFirstVisible] = useState(null)
	const [hasMore, setHasMore] = useState(true)
	const [hasPrevious, setHasPrevious] = useState(false)
	const [cursorStack, setCursorStack] = useState([])
	const [filtered, setFiltered] = useState(false)
	const isFetchingRef = useRef(false)
	const [pageLoading, setPageLoading] = useState(false)
	const [clearFilters, setClearFilters] = useState(false)
	const [jobRequests, setJobRequests] = useState([])
	const [serviceTypeFilter, setServiceTypeFilter] = useState([])
	const [vendorFilterOptions, setVendorFilterOptions] = useState([])
	const [locationFilterOptions, setLocationFilterOptions] = useState([])
	const [vendorFilter, setVendorFilter] = useState([])
	const [statusFilter, setStatusFilter] = useState("")
	const [locationFilter, setLocationFilter] = useState([])
	const [paywallTriggered, setPaywallTriggered] = useState(false)
	const [hasOpened, setHasOpened] = useState(false)
	const [partner, setPartner] = useState(null)

	useEffect(() => {
		setClearFilters(true)
	}, [userPlus.teamId])

	// Reset pagination when user changes
	useEffect(() => {
		if (userPlus && userPlus !== "pending") {
			setPage(1)
			setLastVisible(null)
			setFirstVisible(null)
			setHasMore(true)
			setHasPrevious(false)
			setCursorStack([])
			setFiltered(false)
		}
	}, [userPlus])

	useEffect(() => {
		if (userPlus === "pending" || !userPlus || !userPlus.id) {
			return // Early return if user status is pending or not available
		}



		setPartner(Partners.find((p) => p.id === userPlus.partner))

		const fetchJobRequests = async () => {
			// Prevent concurrent fetches
			if (isFetchingRef.current) {
				return
			}
			
			try {
				isFetchingRef.current = true
				setPageLoading(true)

				// Reset filters if clearFilters flag is true
				if (clearFilters === true) {
					setClearFilters(false)
					setFiltered(false)
					setPage(1)
					setLastVisible(null)
					setFirstVisible(null)
					setHasMore(true)
					setHasPrevious(false)
					setCursorStack([])
					resetFilters()
					return // Exit early after resetting
				}

				// Don't fetch if we're on page 1 and have no cursor (initial load)
				if (page === 1 && !lastVisible && jobRequests.length > 0) {
					setPageLoading(false)
					return
				}

				// Construct the base query
				let q = query(
					collection(db, "jobRequests"),
					where(
						userPlus.isAdmin ? "teamId" : "userId",
						"==",
						userPlus.isAdmin ? userPlus.teamId : userPlus.id
					),
					orderBy("_createdAt", "desc"),
					limit(pageSize + 1)
				)

				// Add cursor for pagination
				if (lastVisible && page > 1 && lastVisible.id) {
					q = query(q, startAfter(lastVisible))
				}

				let doc
				try {
					doc = await getDocs(q)
				} catch (queryError) {
					console.error("Query failed, trying without orderBy:", queryError)
					// Fallback query without orderBy if the main query fails
					const fallbackQuery = query(
						collection(db, "jobRequests"),
						where(
							userPlus.isAdmin ? "teamId" : "userId",
							"==",
							userPlus.isAdmin ? userPlus.teamId : userPlus.id
						),
						limit(pageSize + 1)
					)
					doc = await getDocs(fallbackQuery)
				}
				
				if (!doc.empty) {
					const jobRequestsData = doc.docs.map((doc) => ({
						id: doc.id,
						...doc.data(),
					}))

					// Check if we have more pages
					const hasMorePages = doc.docs.length > pageSize
					
					// Set the data (remove the extra item used for pagination)
					setJobRequests(jobRequestsData.slice(0, pageSize))
					
					// Update cursors
					if (doc.docs.length > 0) {
						// For pagination, we need to set the cursor to the last document that was displayed
						// This will be used as the starting point for the next page
						// We slice to pageSize to get only the documents that are actually displayed
						const displayedDocs = doc.docs.slice(0, pageSize)
						if (displayedDocs.length > 0) {
							const newLastVisible = displayedDocs[displayedDocs.length - 1]
							setLastVisible(newLastVisible)
							setFirstVisible(displayedDocs[0])
						}
					}
					
					// Update pagination state
					setHasMore(hasMorePages)
					setHasPrevious(cursorStack.length > 0)

				} else {
					setJobRequests([])
					setHasMore(false)
					setHasPrevious(false)
				}

				setFiltered(false)
				setPageLoading(false)
			} catch (error) {
				console.error("Error fetching jobRequests:", error)
				setPageLoading(false)
				// Don't redirect to login on pagination errors, just show empty state
				setJobRequests([])
				setHasMore(false)
				setHasPrevious(false)
			} finally {
				isFetchingRef.current = false
			}
		}
		fetchJobRequests()
	}, [userPlus, clearFilters, page])

	const getVendorList = async () => {
		let q
		if (userPlus.isAdmin) {
			q = query(
				collection(db, "jobRequests"),
				where("teamId", "==", userPlus.teamId)
			)
		} else {
			q = query(
				collection(db, "jobRequests"),
				where("userId", "==", userPlus.id)
			)
		}
		const docs = await getDocs(q)
		const vendorIds = [
			...new Set(docs.docs.flatMap((doc) => doc.data().vendorIds)),
		]

		const vendorResults = await Promise.all(
			vendorIds.map(async (item) => {
				const q = query(collection(db, "users"), where("id", "==", item))
				const querySnapshot = await getDocs(q)
				return querySnapshot.docs.map((doc) => doc.data())
			})
		)
		const vendorResultsFlat = vendorResults.flat()
		const vendorOptions = vendorResultsFlat.map((item) => ({
			label: item.name ? item.name : item.email,
			value: item.id,
		}))
		setVendorFilterOptions(vendorOptions)
	}

	const getLocationFilter = async () => {
		const q = query(
			collection(db, "jobRequests"),
			where(
				userPlus.isAdmin ? "teamId" : "userId",
				"==",
				userPlus.isAdmin ? userPlus.teamId : userPlus.id
			)
		)
		const doc = await getDocs(q)
		const locationOptions = Array.from(
			new Set(doc.docs.map((doc) => doc.data().locationAddress))
		).map((item) => ({
			label: item,
			value: item,
		}))
		setLocationFilterOptions(locationOptions)
	}

	const resetFilters = () => {
		setServiceTypeFilter([])
		setVendorFilter([])
		setStatusFilter("")
		setLocationFilter([])
		// Reset pagination when filters are cleared
		setPage(1)
		setLastVisible(null)
		setFirstVisible(null)
		setHasMore(true)
		setHasPrevious(false)
		setCursorStack([])
	}

	const openFilters = async () => {
		if (!hasOpened) {
			await getVendorList()
			await getLocationFilter()
			setHasOpened(true)
		}
		onOpen()
	}

	const getFilteredList = async () => {
		try {
			let uniqueServicesMap = new Map() // Use a Map for uniqueness
			let filteredJobRequests = []
			// Create a base query with the common condition for isActive
			let baseQuery
			if (userPlus.isAdmin) {
				baseQuery = query(
					collection(db, "jobRequests"),
					where("teamId", "==", userPlus.teamId)
				)
			} else {
				baseQuery = query(
					collection(db, "jobRequests"),
					where("userId", "==", userPlus.id)
				)
			}
			// Add conditions based on serviceTypeFilter
			if (serviceTypeFilter.length > 0) {
				baseQuery = query(
					baseQuery,
					where("serviceType", "in", serviceTypeFilter)
				)
			}

			if (locationFilter.length > 0) {
				baseQuery = query(
					baseQuery,
					where("locationAddress", "==", locationFilter)
				)
			}
			let results = []
			// Filter based on vendorFilter
			if (vendorFilter.length > 0) {
				const vendorResults = await Promise.all(
					vendorFilter.map(async (item) => {
						const q = query(
							baseQuery,
							where("vendorIds", "array-contains", item)
						)
						const querySnapshot = await getDocs(q)
						return querySnapshot.docs
					})
				)
				results = [...results, ...vendorResults.flat()]
			}
			if (vendorFilter.length === 0) {
				const doc = await getDocs(baseQuery)
				results = doc.docs
			}
			// Clear the existing uniqueServicesMap
			uniqueServicesMap.clear()

			// Populate the uniqueServicesMap with new data
			results.flat().forEach((doc) => {
				const service = {
					id: doc.id,
					...doc.data(),
				}
				uniqueServicesMap.set(service.id, service)
			})

			filteredJobRequests = Array.from(uniqueServicesMap.values())

			if (statusFilter !== "") {
				if (statusFilter === "scheduled") {
					filteredJobRequests = filteredJobRequests.filter(
						(request) => request.selectedResponseId
					)
				}
				if (statusFilter === "inProgress") {
					filteredJobRequests = filteredJobRequests.filter(
						(request) =>
							request.vendorsAvailable >= 1 && !request.selectedResponseId
					)
				}
				if (statusFilter === "pending") {
					filteredJobRequests = filteredJobRequests.filter(
						(request) => !request.vendorsAvailable
					)
				}
			}
			setFiltered(true)
			setJobRequests(filteredJobRequests)
			// Reset pagination when filters are applied
			setPage(1)
			setLastVisible(null)
			setFirstVisible(null)
			setHasMore(false) // No pagination for filtered results
			setHasPrevious(false)
			setCursorStack([])
			onClose()
		} catch (error) {
			console.error("Error filtering job requests:", error)
		}
	}

	const goToNext = () => {
		if (hasMore && !filtered) {
			// Save current cursor to stack for backward navigation
			if (lastVisible) {
				setCursorStack(prev => [...prev, lastVisible])
			}
			setPage(page + 1)
		}
	}

	const goToPrevious = () => {
		if (page > 1 && !filtered && cursorStack.length > 0) {
			// Get the previous cursor from the stack
			const previousCursor = cursorStack[cursorStack.length - 1]
			const newStack = cursorStack.slice(0, -1)
			
			setCursorStack(newStack)
			setLastVisible(previousCursor)
			setPage(page - 1)
			setHasPrevious(newStack.length > 0)
		}
	}

	const startNewJobRequest = () => {
		if (
			userPlus.userType === "agent" &&
			!userPlus.activePlan &&
			!userPlus.partner &&
			userPlus.jobRequestsMade >= JOB_REQUEST_LIMIT
		) {
			setPaywallTriggered(true)
			return
		} else {
			navigate("/job-requests/new")
		}
	}

	const statusFilterOptions = [
		{ label: "Request Booking Jobs", value: "scheduled" },
		{ label: "Jobs With Bids Received", value: "inProgress" },
		{ label: "Requests Awaiting Responses", value: "pending" },
		{ label: "All Job Requests", value: "" },
	]

	if (userPlus === null) {
		navigate("/login")
	}

	return (
		<>
			{userPlus.planName === null && userPlus.userType === "agent" && (
				<PaywallBanner location="AgentJobRequestList" />
			)}
			<Box
				ml={{ base: "15px", lg: "93px" }}
				mr={{ base: "15px", lg: "93px" }}
				mt="30px"
			>
				<Heading size="lg" fontWeight="bold">
					{partner ? partner?.jobRequestHeader : "Job Requests"}
				</Heading>
				<Button
					onClick={() => startNewJobRequest()}
					display={["flex", "flex", "none", "none"]}
					mt="20px"
					w="100%"
					bgColor="borderColor"
					color="white"
				>
					{userPlus.partner && partner
						? partner.jobRequestCopy
						: "Contact Vendors"}
				</Button>
				<HStack
					display={["none", "none", "flex", "flex"]}
					justifyContent="space-between"
					mt="10px"
				>
					<Text>
						Track and manage your open{" "}
						{partner?.jobRequestText ? partner.jobRequestText : "job requests"}
					</Text>
					<Button
						onClick={() => startNewJobRequest()}
						bgColor="borderColor"
						color="white"
					>
						{userPlus.partner && partner
							? partner.jobRequestCopy
							: "Contact Vendors"}
					</Button>
				</HStack>
				<Box
					display={["flex", "flex", "none", "none"]}
					mt="20px"
					flexDirection="column"
				>
					<Box display="flex" justifyContent="flex-end" mb="15px">
						<Button
							onClick={() => openFilters()}
							rightIcon={<UpDownIcon />}
							bgColor="background"
							mt="15px"
						>
							Filter
						</Button>
						<Button onClick={() => setClearFilters(true)} mt="15px">
							Clear Filters
						</Button>
					</Box>
					{jobRequests.length === 0 && (
						<EmptyState
							helperText={`You have not submitted any ${
								partner?.jobRequestText
									? partner.jobRequestText
									: "job requests"
							} yet!`}
						/>
					)}
					{jobRequests.map((jobRequest, index) => {
						return (
							<JobRequestsMobileCard
								jobRequest={jobRequest}
								index={index}
								key={index}
							/>
						)
					})}
				</Box>
				<Box
					display={["none", "none", "flex", "flex"]}
					flexDirection="column"
					mt="30px"
				>
					<Box display="flex" justifyContent="flex-end" gap={4}>
						<Button onClick={() => setClearFilters(true)} mt="15px">
							Clear Filters
						</Button>
						<Button
							onClick={() => openFilters()}
							rightIcon={<UpDownIcon />}
							bgColor="blue.900"
							color="white"
							mt="15px"
						>
							Filter
						</Button>
					</Box>
					{pageLoading && <SkeletonTable />}
					{!pageLoading && (
						<Table variant="simple" mt="40px">
							<Thead>
								<Tr>
									<Th>Location Address</Th>
									<Th>Service Type</Th>
									<Th>Preferred Dates</Th>
									<Th>Job Status</Th>
									<Th>Details</Th>
								</Tr>
							</Thead>
							<Tbody>
								{jobRequests.map((jobRequest) => {
									return (
										<>
											<Tr key={jobRequest.id}>
												<Td>{jobRequest.locationAddress}</Td>
												<Td>{jobRequest.serviceType}</Td>
												<Td>
													{jobRequest.servicePreferredDates &&
														jobRequest.servicePreferredDates.map(
															(entry, index) => (
																<HStack gap={5} mb="5px" key={index}>
																	<Text key={index}>
																		{dateToFormat(entry.date)}
																	</Text>
																</HStack>
															)
														)}
												</Td>
												{!jobRequest.blackDomeStatus && (
													<Td>
														{jobRequest.selectedResponseId && (
															<Tag
																borderRadius="full"
																variant="solid"
																bgColor="green"
																mr="5px"
																mb="5px"
															>
																<TagLabel>Request Booking</TagLabel>
															</Tag>
														)}
														{jobRequest.vendorsAvailable >= 1 &&
															!jobRequest.selectedResponseId && (
																<Tag
																	borderRadius="full"
																	variant="solid"
																	bgColor="
														#FDE8C8"
																	color="black"
																	mr="5px"
																	mb="5px"
																>
																	<TagLabel>In Progress</TagLabel>
																</Tag>
															)}
														{!jobRequest.vendorsAvailable && (
															<Tag
																borderRadius="full"
																variant="solid"
																bgColor="
													#gray.500"
																mr="5px"
																mb="5px"
															>
																<TagLabel>Awaiting Responses</TagLabel>
															</Tag>
														)}
													</Td>
												)}
												{jobRequest.blackDomeStatus && (
													<Td>
														<Tag
															borderRadius="full"
															variant="solid"
															bgColor={
																BlackDomeStatuses.find(
																	(status) =>
																		status.value === jobRequest.blackDomeStatus
																).color
															}
															mr="5px"
															mb="5px"
														>
															<TagLabel>
																{
																	BlackDomeStatuses.find(
																		(status) =>
																			status.value ===
																			jobRequest.blackDomeStatus
																	).label
																}
															</TagLabel>
														</Tag>
													</Td>
												)}
												<Td>
													<IconButton
														as={NavLink}
														bgColor="background"
														to={`/job-requests/responses/${jobRequest.id}`}
														aria-label="View Job Request Details"
														icon={<EditIcon />}
													/>
												</Td>
											</Tr>
										</>
									)
								})}
							</Tbody>
						</Table>
					)}
					{jobRequests.length === 0 && (
						<EmptyState
							helperText={`You have not submitted any ${
								partner?.jobRequestText
									? partner.jobRequestText
									: "job requests"
							} yet!`}
						/>
					)}
				</Box>
				{jobRequests.length > 0 && (hasMore || hasPrevious) && (
					<Box display="flex" justifyContent="flex-end" gap={3} my="15px">
						<Button
							onClick={() => goToPrevious()}
							isDisabled={!hasPrevious || filtered}
						>
							Previous
						</Button>
						<Text fontSize="sm" color="gray.600" alignSelf="center">
							Page {page}
						</Text>
						<Button
							onClick={() => goToNext()}
							isDisabled={!hasMore || filtered}
						>
							Next
						</Button>
					</Box>
				)}
			</Box>
			<Modal
				isOpen={isOpen}
				onClose={onClose}
				size={{ base: "full", lg: "2xl" }}
			>
				<ModalOverlay />
				<ModalContent>
					<ModalHeader>Filter Job Requests</ModalHeader>
					<ModalCloseButton />
					<ModalBody>
						<Stack gap={2}>
							<Box>
								<Select
									placeholder="Location Address"
									value={locationFilterOptions.filter((option) =>
										locationFilter.includes(option.value)
									)}
									options={locationFilterOptions}
									onChange={(option) => {
										setLocationFilter(option.value)
									}}
								/>
							</Box>
							<Box>
								<Select
									placeholder="Service Type"
									value={serviceTypesOptions.filter((option) =>
										serviceTypeFilter.includes(option.value)
									)}
									isMulti
									options={serviceTypesOptions}
									onChange={(options) => {
										setServiceTypeFilter(options.map((option) => option.value))
									}}
								/>
							</Box>
							<Box>
								<Select
									placeholder="Vendors"
									value={vendorFilterOptions.filter((option) =>
										vendorFilter.includes(option.value)
									)}
									isMulti
									options={vendorFilterOptions}
									onChange={(options) => {
										setVendorFilter(options.map((option) => option.value))
									}}
								/>
							</Box>
							<Box>
								<Select
									placeholder="Job Request Status"
									value={statusFilterOptions.filter(
										(option) => option.value === statusFilter
									)}
									options={statusFilterOptions}
									onChange={(options) => {
										setStatusFilter(options.value)
									}}
								/>
							</Box>
						</Stack>
					</ModalBody>
					<ModalFooter margin="auto">
						<Button
							bgColor="borderColor"
							color="white"
							onClick={getFilteredList}
						>
							Apply Filters
						</Button>
					</ModalFooter>
				</ModalContent>
			</Modal>
			<AgentPayWallModal
				isOpen={paywallTriggered}
				setPaywallTriggered={setPaywallTriggered}
			/>
		</>
	)
}

export default React.memo(AgentJobRequestListView)
